/* eslint-disable no-undef */
// Cordova is available globally
// Location permission request comes from
// https://github.com/dpa99c/cordova-plugin-request-location-accuracy#full-android--ios-example
import { Capacitor } from '@capacitor/core';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { Geolocation } from '@capacitor/geolocation';
import { telemetry } from '../backend';
import { getStore } from '../store';
import { actions } from '../store/modules/log-trips';

function onError(error) {
  telemetry(
    telemetry.ERROR,
    `[locationPermission] received error of ${JSON.stringify(error)}`,
  );
}

const promisify =
  (f) =>
  (...args) =>
    new Promise((resolve, reject) => {
      f(resolve, reject, ...args);
    });

export const isPhysicalActivityPermissionDenied = async () => {
  try {
    if (!Capacitor.isNativePlatform()) return false;

    const platform = Capacitor.getPlatform();

    if (platform === 'android') {
      const promisifiedPermissionAuthorizationStatus = promisify(
        cordova.plugins.diagnostic.getPermissionAuthorizationStatus,
      );
      const physicalActivityPermissionStatus =
        await promisifiedPermissionAuthorizationStatus(
          cordova.plugins.diagnostic.permission.ACTIVITY_RECOGNITION,
        );
      if (
        physicalActivityPermissionStatus ===
          cordova.plugins.diagnostic.permissionStatus.DENIED ||
        physicalActivityPermissionStatus ===
          cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS
      ) {
        getStore().dispatch(actions.setPhysicalActivityAllowed());
        return true;
      }
      getStore().dispatch(actions.setPhysicalActivityDenied());
      return false;
    }
    if (platform === 'ios') {
      const promisifiedGetMotionAuthorizationStatus = promisify(
        cordova.plugins.diagnostic.getMotionAuthorizationStatus,
      );

      const motionAuthorizationStatus =
        await promisifiedGetMotionAuthorizationStatus();
      if (
        motionAuthorizationStatus ===
          cordova.plugins.diagnostic.permissionStatus.DENIED ||
        motionAuthorizationStatus ===
          cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS
      ) {
        getStore().dispatch(actions.setPhysicalActivityAllowed());
        return true;
      }
      getStore().dispatch(actions.setPhysicalActivityDenied());
      return false;
    }

    return false;
  } catch (error) {
    onError(error);
    return true;
  }
};

export const getPhysicalActivityPermissionStatus = async () => {
  try {
    if (!Capacitor.isNativePlatform()) return false;

    const platform = Capacitor.getPlatform();

    if (platform === 'android') {
      const promisifiedPermissionAuthorizationStatus = promisify(
        cordova.plugins.diagnostic.getPermissionAuthorizationStatus,
      );
      const physicalActivityPermissionStatus =
        await promisifiedPermissionAuthorizationStatus(
          cordova.plugins.diagnostic.permission.ACTIVITY_RECOGNITION,
        );
      if (
        physicalActivityPermissionStatus ===
        cordova.plugins.diagnostic.permissionStatus.GRANTED
      ) {
        getStore().dispatch(actions.setPhysicalActivityAllowed());
        return true;
      }
      getStore().dispatch(actions.setPhysicalActivityDenied());
      return false;
    }
    if (platform === 'ios') {
      const promisifiedGetMotionAuthorizationStatus = promisify(
        cordova.plugins.diagnostic.getMotionAuthorizationStatus,
      );

      const motionAuthorizationStatus =
        await promisifiedGetMotionAuthorizationStatus();
      if (
        motionAuthorizationStatus ===
        cordova.plugins.diagnostic.permissionStatus.GRANTED
      ) {
        getStore().dispatch(actions.setPhysicalActivityAllowed());
        return true;
      }
      getStore().dispatch(actions.setPhysicalActivityDenied());
      return false;
    }

    return false;
  } catch (error) {
    onError(error);
    return false;
  }
};

export const makePhysicalActivityPermissionRequest = async () => {
  // Check if status is already set before making request
  const physicalActivityPermissionStatus =
    await getPhysicalActivityPermissionStatus();
  if (physicalActivityPermissionStatus) return;
  try {
    if (!Capacitor.isNativePlatform()) return;
    const platform = Capacitor.getPlatform();
    if (platform === 'android') {
      const promisifiedRequestRuntimePermission = promisify(
        cordova.plugins.diagnostic.requestRuntimePermission,
      );

      await promisifiedRequestRuntimePermission(
        cordova.plugins.diagnostic.permission.ACTIVITY_RECOGNITION,
      );
    }
    if (platform === 'ios') {
      // TODO: Remove (make sure android is done too)
      // const promisifiedRequestMotionAuthorization = promisify(
      //   cordova.plugins.diagnostic.requestMotionAuthorization,
      // );
      // await promisifiedRequestMotionAuthorization();
      await DeviceMotionEvent.requestPermission();
    }

    await getPhysicalActivityPermissionStatus();
  } catch (error) {
    onError(error);
  }
};

const makeLocationServicesRequest = async () => {
  try {
    // Make the request to enable location services
    if (!Capacitor.isNativePlatform()) return;

    // Check whether we are right to request location services
    const canRequest = true;

    if (canRequest) {
      // Request location services be enabled
      await Geolocation.requestPermissions(
        'location', // iOS ignores this
      );
    }
  } catch (error) {
    onError(error);
  }
};

const handleLocationAuthorizationStatus = async (status) => {
  let platform;
  try {
    if (!Capacitor.isNativePlatform()) return;
    platform = Capacitor.getPlatform();
  } catch (e) {
    onError();
    return;
  }

  const {
    AUTHORIZATION_STATUS_ALWAYS,
    AUTHORIZATION_STATUS_DENIED,
    AUTHORIZATION_STATUS_RESTRICTED,
    AUTHORIZATION_STATUS_NOT_DETERMINED,
    AUTHORIZATION_STATUS_WHEN_IN_USE,
  } = BackgroundGeolocation;

  switch (status) {
    case AUTHORIZATION_STATUS_ALWAYS:
      if (platform === 'ios') {
        onError('Location services is already switched ON');
      } else {
        await makeLocationServicesRequest();
      }
      break;
    case AUTHORIZATION_STATUS_NOT_DETERMINED:
      // eslint-disable-next-line no-use-before-define
      await requestLocationAuthorization();
      break;
    case AUTHORIZATION_STATUS_DENIED:
      if (platform === 'android') {
        onError('User denied permission to use location');
      } else {
        await makeLocationServicesRequest();
      }
      break;
    case AUTHORIZATION_STATUS_RESTRICTED:
      // Caveat https://github.com/dpa99c/cordova-plugin-request-location-accuracy#ios-cancel-button-caveat
      // If a user presses cancel when prompted to go to settings
      // We cannot prompt them to go to settings EVER AGAIN D:
      // Thus best we can do is show the dialog at the top
      onError('User denied permission to use location always');
      break;
    case AUTHORIZATION_STATUS_WHEN_IN_USE:
      if (platform === 'android') {
        await makeLocationServicesRequest();
      } else {
        onError('Granted when in use');
      }
      break;
    default:
      onError('Could not determine permission status');
      break;
  }
};

export const requestLocationAuthorization = async () => {
  try {
    // This is for requesting permissions to allow Deckee to access the location
    if (!Capacitor.isNativePlatform()) return;
    // const promisifiedRequestLocationAuthorization = promisify(
    //   cordova.plugins.diagnostic.requestLocationAuthorization,
    // );
    const { status } = await BackgroundGeolocation.requestPermission();
    await handleLocationAuthorizationStatus(status);
  } catch (error) {
    onError(error);
  }
};

export const isLocationEnabled = async () => {
  try {
    // This is for determining if location services is enabled
    if (!Capacitor.isNativePlatform()) return false;

    // Check permissions throws an error if location services aren't available
    await Geolocation.checkPermissions();
    return true;
  } catch (error) {
    onError(error);
    return false;
  }
};

export const isLocationAvailable = async () => {
  if (!('Capacitor' in window)) throw new Error('Capacitor not available');

  const { location, courseLocation } = await Geolocation.checkPermissions();
  return location === 'granted' || courseLocation === 'granted';
};

const requestLocationAccuracy = async () => {
  try {
    // This is for requesting that location services be enabled
    if (!Capacitor.isNativePlatform()) return false;

    const { status } = await BackgroundGeolocation.getProviderState();

    await handleLocationAuthorizationStatus(status);
    return status;
  } catch (error) {
    onError(error);
  }
  return true;
};

export { requestLocationAccuracy as requestLocationPermission };

export const initialize = () => {};
